import axios from 'axios';
import poliConfig from './poliConfig';
import { heimdallInstance } from '../services/auth/heimdall/axios-config';
import { localStorageService } from 'src/services/local-storage.service';

export const poliFoundationRequest = axios.create({
  baseURL: poliConfig.poliFoundationUrl,
});
poliFoundationRequest.interceptors.request.use(function (config) {
  // Sempre pega o token mais atual do localStorage
  const tokenInfo = localStorageService.getLocalStorageItem({
    key: 'user_token_login_info',
  });
  const currentToken = tokenInfo?.token ?? '';

  config.headers.Authorization = `Bearer ${currentToken}`;
  config.headers['Content-Type'] =
    config.headers['Content-Type'] || 'application/json';
  config.headers['Accept'] = 'application/json';
  return config;
});

// Interceptor para atualizar o token quando expirar e fazer a requisição novamente
poliFoundationRequest.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;

    // Se o token expirou, atualiza o token e faz a requisição novamente
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const tokenInfo = localStorageService.getLocalStorageItem({
          key: 'user_token_login_info',
        });

        // Validação rigorosa: verificar se refresh_token existe, é string e não está vazio
        if (!tokenInfo) {
          return Promise.reject(
            new Error(
              'Dados de autenticação não encontrados. Faça login novamente.'
            )
          );
        }

        if (!tokenInfo.refresh_token) {
          return Promise.reject(
            new Error('Refresh token não encontrado. Faça login novamente.')
          );
        }

        if (typeof tokenInfo.refresh_token !== 'string') {
          return Promise.reject(
            new Error(
              'Refresh token inválido (não é string). Faça login novamente.'
            )
          );
        }

        const currentRefreshToken = tokenInfo.refresh_token.trim();

        if (currentRefreshToken === '') {
          // Tentar recuperar novamente do localStorage antes de falhar
          const retryTokenInfo = localStorageService.getLocalStorageItem({
            key: 'user_token_login_info',
          });

          if (
            retryTokenInfo?.refresh_token &&
            retryTokenInfo.refresh_token.trim() !== ''
          ) {
            const retryRefreshToken = retryTokenInfo.refresh_token.trim();

            const refreshResponse = await heimdallInstance.post(
              `/auth/refresh-token`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${retryRefreshToken}`,
                },
              }
            );

            // Processar resposta (código duplicado, mas necessário para a segunda tentativa)
            if (refreshResponse.status === 200 && refreshResponse.data) {
              if (
                !refreshResponse.data.token ||
                !refreshResponse.data.refresh_token
              ) {
                return Promise.reject(
                  new Error('Resposta do refresh token inválida')
                );
              }

              localStorageService.setLocalStorageItem({
                key: 'user_token_login_info',
                value: refreshResponse.data,
              });

              originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.token}`;
              return poliFoundationRequest(originalRequest);
            }
          }

          return Promise.reject(
            new Error('Refresh token está vazio. Faça login novamente.')
          );
        }

        let refreshResponse;
        try {
          refreshResponse = await heimdallInstance.post(
            `/auth/refresh-token`,
            {},
            {
              headers: {
                Authorization: `Bearer ${currentRefreshToken}`,
              },
            }
          );
        } catch (refreshRequestError) {
          console.error('refreshRequestError: ', refreshRequestError);

          throw refreshRequestError;
        }

        if (refreshResponse.status === 200 && refreshResponse.data) {
          // Verifica se a resposta tem a estrutura esperada
          if (
            !refreshResponse.data.token ||
            !refreshResponse.data.refresh_token
          ) {
            return Promise.reject(
              new Error('Resposta do refresh token inválida')
            );
          }

          // Atualiza o token no localStorage usando o serviço correto
          localStorageService.setLocalStorageItem({
            key: 'user_token_login_info',
            value: refreshResponse.data,
          });

          // Atualiza o token na requisição original
          originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.token}`;

          // TODO: atualizar o session_id no localStorage do poli_access
          // no poli_access o TOKEN  é o LEGACY_TOKEN do user_token_login_info
          const poliAccess = JSON.parse(
            window.localStorage.getItem('poli_access')
          );
          poliAccess.session_id = refreshResponse.data.session_id;
          localStorageService.setLocalStorageItem(
            'poli_access',
            JSON.stringify(poliAccess)
          );

          // Refaz a requisição com o novo token
          return poliFoundationRequest(originalRequest);
        } else {
          return Promise.reject(
            new Error('Falha ao renovar token. Faça login novamente.')
          );
        }
      } catch (refreshError) {
        console.error('refreshError: ', refreshError);

        // Se o refresh falhar (ex: refresh_token expirado), limpa o localStorage
        // para forçar novo login
        if (
          refreshError?.response?.status === 401 ||
          refreshError?.response?.status === 403
        ) {
          // Verificar se o erro é porque o refresh_token está vazio
          const errorMessage = refreshError?.message || '';
          if (
            errorMessage.includes('vazio') ||
            errorMessage.includes('não encontrado') ||
            errorMessage.includes('não disponível')
          ) {
            // Não limpa o localStorage aqui, pois pode ser um problema temporário
            return;
          } else {
            localStorageService.removeLocalStorageItem({
              key: 'user_token_login_info',
            });
            window.localStorage.removeItem('poli_access');
          }
        }
        // Se o refresh_token falhar mas ainda houver autenticação válida,
        // não propaga o erro imediatamente para evitar mostrar modal de sessão expirada
        const hasValidAuth = localStorageService.getLocalStorageItem({
          key: 'user_token_login_info',
        });

        if (hasValidAuth && hasValidAuth.refresh_token) {
          // Retorna o erro original (401) para que o componente possa tratar
          // Mas não limpa o localStorage se ainda houver autenticação válida
          return Promise.reject(error);
        }

        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default poliFoundationRequest;
